import {
  ApolloClient,
  DocumentNode,
  HttpLink,
  InMemoryCache,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { setVerbosity } from 'ts-invariant';

if (process.env.NODE_ENV === 'development') {
  loadDevMessages();
  loadErrorMessages();
  setVerbosity('debug');
}

// Determine if we're on the client or server side
const isClient = typeof window !== 'undefined';

// Debug flag for verbose Apollo logging (opt-in via environment variable)
const DEBUG_APOLLO = process.env.DEBUG_APOLLO === 'true';

// Use NEXT_PUBLIC_ variables for client-side, regular variables for server-side
const STRAPI_URL = isClient
  ? process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337'
  : process.env.STRAPI_URL || process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';
const GRAPHQL_ENDPOINT = `${STRAPI_URL}/graphql`;

if (DEBUG_APOLLO) {
  // eslint-disable-next-line no-console
  console.log('🔍 Apollo Client Debug Info:');
  // eslint-disable-next-line no-console
  console.log('Is Client:', isClient);
  // eslint-disable-next-line no-console
  console.log('STRAPI_URL:', STRAPI_URL);
  // eslint-disable-next-line no-console
  console.log('GRAPHQL_ENDPOINT:', GRAPHQL_ENDPOINT);
}

// Create the auth link for both client and server
const authLink = setContext(async (_, { headers }) => {
  // Use NEXT_PUBLIC_ token for client-side, regular token for server-side
  const token = isClient
    ? process.env.NEXT_PUBLIC_STRAPI_API_TOKEN
    : process.env.STRAPI_API_TOKEN || process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;
  const authHeader = token ? `Bearer ${token}` : '';

  if (DEBUG_APOLLO) {
    // eslint-disable-next-line no-console
    console.log('🔐 Auth Link Debug:');
    // eslint-disable-next-line no-console
    console.log('Is Client:', isClient);
    // eslint-disable-next-line no-console
    console.log('Token length:', token?.length || false);
  }

  // For server-side requests, add cache control headers to ensure fresh data
  const serverHeaders = !isClient
    ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      }
    : {};

  return {
    headers: {
      ...headers,
      ...serverHeaders,
      Authorization: authHeader,
    },
  };
});

// Create the HTTP link
const httpLink = new HttpLink({
  uri: `${GRAPHQL_ENDPOINT}`,
  credentials: 'same-origin',
});

// Client-side Apollo Client
let clientSideClient: ApolloClient<unknown> | null = null;

export function getClientSideClient() {
  if (!clientSideClient) {
    clientSideClient = new ApolloClient({
      cache: new InMemoryCache(),
      link: authLink.concat(httpLink),
      defaultOptions: {
        query: {
          // Use cache-and-network for client-side: shows cached data immediately,
          // then fetches fresh data in the background
          fetchPolicy: 'cache-and-network' as any,
        },
      },
    });
  }
  return clientSideClient;
}

// Server-side query function
export async function query<T>({
  query,
  variables,
}: {
  query: DocumentNode | TypedDocumentNode<T, OperationVariables>;
  variables?: OperationVariables | undefined;
}) {
  const queryBody = query.loc?.source.body || '';
  if (DEBUG_APOLLO) {
    // eslint-disable-next-line no-console
    console.log('🚀 Server-side query execution:');
    // eslint-disable-next-line no-console
    console.log('Query preview:', queryBody.substring(0, 100).trim() + '...');
    // eslint-disable-next-line no-console
    console.log('Variables:', variables);
  }

  // Always use network-only on the server to ensure fresh data
  // Create a new client instance for each query to ensure no shared cache state
  // Use an InMemoryCache with disabled caching policies
  const client = new ApolloClient({
    cache: new InMemoryCache({
      // Disable all caching - don't store any query results
      typePolicies: {
        Query: {
          fields: {
            // Disable caching for known fields
            pageCategories: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            landingPages: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            pages: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            resourceArticles: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            resourceCategories: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            resourceCenter: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            siteSetting: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
            menus: {
              merge: false,
              read: () => undefined, // Never read from cache
            },
          },
        },
      },
    }),
    link: authLink.concat(httpLink),
    ssrMode: true,
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only', // Always fetch from network, never use cache
      },
      watchQuery: {
        fetchPolicy: 'network-only',
      },
    },
  });

  try {
    const result = await client.query({
      query,
      variables,
      fetchPolicy: 'network-only', // Explicitly set to bypass cache
      // Add HTTP cache control headers to prevent any HTTP-level caching
      context: {
        fetchOptions: {
          cache: 'no-store', // HTTP-level cache control (fetch API option)
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      },
    });

    // Check for GraphQL errors even if the query "succeeded"
    if (result.errors && result.errors.length > 0) {
      console.error('❌ GraphQL returned errors:', JSON.stringify(result.errors, null, 2));
      // Log query details when there are errors
      console.error('Query:', queryBody.substring(0, 100).trim() + '...');
      console.error('Variables:', variables);
    }

    if (DEBUG_APOLLO) {
      // eslint-disable-next-line no-console
      console.log('✅ Query successful');
    }
    return { data: result.data };
  } catch (error) {
    console.error('❌ Error executing query:', JSON.stringify(error, null, 2));

    // Log the URL being used - this helps diagnose connection issues
    console.error('🌐 Connection Details:');
    console.error('   STRAPI_URL:', STRAPI_URL);
    console.error('   GRAPHQL_ENDPOINT:', GRAPHQL_ENDPOINT);
    console.error('   Is Client:', isClient);
    console.error('   Environment:', {
      STRAPI_URL: process.env.STRAPI_URL,
      NEXT_PUBLIC_STRAPI_URL: process.env.NEXT_PUBLIC_STRAPI_URL,
    });

    // Log network error details
    if ((error as any)?.networkError) {
      const networkError = (error as any).networkError;
      console.error('📡 Network Error Details:', {
        statusCode: networkError.statusCode,
        statusText: networkError.response?.statusText,
      });

      // Log the underlying cause if it's a DNS/connection error
      if (networkError.cause) {
        const cause = networkError.cause as Record<string, unknown>;
        console.error('🔍 Network Error Cause:');
        console.error('   Code:', cause.code);
        console.error('   Message:', cause.message);
        if ('hostname' in cause) {
          console.error('   Hostname:', cause.hostname);
          console.error(
            '   ⚠️  This hostname cannot be resolved. If using Docker service names (e.g., "strapi"),'
          );
          console.error(
            '      they only work inside Docker networks. For local builds, use "localhost" instead.'
          );
        }
        if ('syscall' in cause) {
          console.error('   Syscall:', cause.syscall);
        }
        if ('errno' in cause) {
          console.error('   Errno:', cause.errno);
        }
      }

      // If there are errors in the result, log them
      if (networkError.result?.errors) {
        console.error('🚨 GraphQL Errors from Network Error:');
        networkError.result.errors.forEach((gqlError: any, index: number) => {
          console.error(`  Error ${index + 1}:`, JSON.stringify(gqlError, null, 2));
        });
      }
    }

    // Log the actual error message from GraphQL errors
    if ((error as any)?.graphQLErrors) {
      console.error('🚨 GraphQL Errors:');
      (error as any).graphQLErrors.forEach((gqlError: any, index: number) => {
        console.error(`  Error ${index + 1}:`, {
          message: gqlError.message,
          path: gqlError.path,
          extensions: gqlError.extensions,
        });
      });
    }

    throw error;
  }
}
